import React, { useCallback, useEffect, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import EmptyState from "@/components/EmptyState";
import UserRow from "@/components/discover/UserRow";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Loader2, Users, SearchX } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { useLanguage } from "@/context/LanguageContext";

const PAGE = 20;

function LoadingRows() {
  return (
    <div className="divide-y divide-slate-50" data-testid="discover-loading">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-3 p-4">
          <Skeleton className="h-11 w-11 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-20" />
          </div>
          <Skeleton className="h-8 w-20 rounded-full" />
        </div>
      ))}
    </div>
  );
}

export default function DiscoverPage() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const debounceRef = useRef(null);
  const { subscribe } = useRealtime();
  const { t } = useLanguage();
  const searching = q.trim().length > 0;

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      if (query.trim()) {
        const { data } = await api.get("/users/search", { params: { q: query } });
        setResults(data);
        setHasMore(false);
      } else {
        const { data } = await api.get("/users/suggestions", { params: { limit: PAGE, skip: 0 } });
        setResults(data.items);
        setHasMore(data.has_more);
        setTotal(data.total);
      }
    } catch (e) {
      toast.error(t("Couldn't load people right now. Check your connection and try again."));
    }
    setLoading(false);
  }, [t]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/users/suggestions", { params: { limit: PAGE, skip: results.length } });
      setResults((r) => [...r, ...data.items.filter((n) => !r.some((x) => x.user_id === n.user_id))]);
      setHasMore(data.has_more);
    } catch (e) {
      toast.error(t("Couldn't load more people. Please try again."));
    }
    setLoadingMore(false);
  };

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(q), q ? 350 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [q, load]);

  // Relationship changes elsewhere (accept/block/unfriend) arrive over the shared socket.
  useEffect(() => subscribe((evt) => {
    if (["friend_request", "friend_accepted", "follow_request", "follow_accepted"].includes(evt.type)) load(q);
  }), [subscribe, load, q]);

  // Patch the row instantly, then reconcile with the server.
  const act = async (u, fn, successMsg, optimistic) => {
    setBusy(u.user_id);
    try {
      await fn();
      if (optimistic) {
        setResults((r) => r.map((x) => (x.user_id === u.user_id ? { ...x, relationship: optimistic } : x)));
      }
      if (successMsg) toast.success(successMsg);
      load(q);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy(null);
  };

  const addFriend = (u) =>
    act(u, () => api.post("/friends/requests", { username: u.username }), `Friend request sent to @${u.username}`, "request_sent");

  const acceptFrom = (u) =>
    act(
      u,
      async () => {
        const { data } = await api.get("/friends/requests/incoming");
        const req = data.find((r) => r.user.user_id === u.user_id);
        if (!req) throw { response: { data: { detail: "That request is no longer pending." } } };
        await api.post(`/friends/requests/${req.request_id}/accept`);
      },
      `You're now friends with @${u.username}`,
      "friends"
    );

  const blockUser = (u) =>
    act(u, () => api.post("/blocks", { username: u.username }), `@${u.username} blocked`, "blocked_by_me");

  const unblock = (u) => act(u, () => api.delete(`/blocks/${u.user_id}`), `@${u.username} unblocked`, "none");

  return (
    <AppShell>
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
          {t("Discover People")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {searching ? t("Search real people by their @username.") : t("Real members of Yaprio — online first.")}
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("Search by username…")}
          data-testid="input-search-users"
          className="h-13 rounded-full border-slate-100 bg-white py-6 pl-12 text-base shadow-sm transition-shadow focus-visible:shadow-md"
        />
      </div>

      <Card className="overflow-hidden rounded-3xl border-slate-100 shadow-sm">
        {loading ? (
          <LoadingRows />
        ) : results.length === 0 ? (
          <EmptyState
            icon={searching ? SearchX : Users}
            title={searching ? `${t("No one matches")} “${q.trim()}”` : t("No one else here yet")}
            description={
              searching
                ? t("Usernames only — check the spelling, or try a shorter search.")
                : t("You're early! As more people join Yaprio they'll show up right here.")
            }
            action={
              searching ? (
                <Button variant="outline" onClick={() => setQ("")} data-testid="btn-clear-search" className="rounded-full font-semibold">
                  {t("Clear search")}
                </Button>
              ) : null
            }
            testId="discover-empty"
          />
        ) : (
          <>
            {!searching && total > 0 && (
              <p className="border-b border-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                {total} {total === 1 ? t("member") : t("members")} {t("on Yaprio")}
              </p>
            )}
            <div className="divide-y divide-slate-50" data-testid="search-results">
              {results.map((u) => (
                <UserRow
                  key={u.user_id}
                  user={u}
                  busy={busy === u.user_id}
                  onAdd={addFriend}
                  onAccept={acceptFrom}
                  onBlock={blockUser}
                  onUnblock={unblock}
                />
              ))}
            </div>
            {hasMore && (
              <div className="border-t border-slate-50 p-4">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="btn-load-more-users"
                  className="w-full rounded-full font-semibold"
                >
                  {loadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {t("Load more people")}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </AppShell>
  );
}
